import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

import { StyledAnchor } from "@/components/common/styles";
import {
  ACCENT,
  SITE_STEPS,
  marketingPalette,
} from "@/components/marketing/tokens";
import { FOOTER_BRAND } from "./footerContent";

/** The artwork is wider than its box, so the box letterboxes it. */
const WORDMARK = { width: 192, height: 50 };
/** The one green that carries the offer; no palette role holds it. */
const OFFER_HIGHLIGHT = "#5EF8D9";
/** The offer card's edge against the band. */
const OFFER_EDGE = "#0A2430";
const EASE = "0.3s cubic-bezier(0.23, 1, 0.32, 1)";

/** Both rows are one published control under two modifiers. */
const ROW = {
  display: "flex",
  alignItems: "center",
  width: 330,
  maxWidth: "100%",
  borderRadius: "12px",
  transition: EASE,
};

const ICON = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "17.6px",
  height: "17.6px",
};

const LABEL = {
  fontSize: "13.4px",
  fontWeight: 400,
  lineHeight: "20.1px",
  color: "common.white",
  // Inert on a block box, and left behind by the control's base rule. Kept so
  // the two sides read alike to a probe that walks computed styles.
  alignItems: "center",
  justifyContent: "center",
};

function ExternalIcon() {
  return (
    <Box component="svg" viewBox="0 0 20 20" fill="none" sx={ICON}>
      <path
        d="M15 5L5 15"
        stroke="currentColor"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.66602 5H14.9993V13.3333"
        stroke="currentColor"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

function ExtensionIcon() {
  return (
    <Box component="svg" viewBox="0 0 20 20" fill="none" sx={ICON}>
      <path
        d="M1.66602 17.4076V13.7034H3.51768C3.78717 13.7033 4.0534 13.6444 4.2978 13.5309C4.5422 13.4173 4.75891 13.2519 4.93281 13.046C5.10671 12.8401 5.23362 12.5988 5.3047 12.3389C5.37577 12.0789 5.3893 11.8066 5.34435 11.5409C5.25518 11.0984 5.01305 10.7015 4.66044 10.4197C4.30782 10.138 3.86725 9.98939 3.41602 10.0001H1.66602V6.29672C1.66591 6.17507 1.68977 6.05458 1.73625 5.94216C1.78273 5.82974 1.85091 5.72758 1.93689 5.64152C2.02287 5.55546 2.12497 5.48719 2.23736 5.44061C2.34974 5.39403 2.4702 5.37005 2.59185 5.37005H6.29602V3.51838C6.29601 3.24848 6.35496 2.98183 6.46875 2.73708C6.58253 2.49233 6.74839 2.27539 6.95475 2.10142C7.1611 1.92744 7.40296 1.80064 7.66343 1.72988C7.92389 1.65911 8.19666 1.64609 8.46268 1.69172C8.90394 1.78167 9.29957 2.0238 9.58045 2.37581C9.86133 2.72782 10.0096 3.16733 9.99935 3.61755V5.36755H13.7027C13.9485 5.36755 14.1842 5.46518 14.3579 5.63897C14.5317 5.81275 14.6293 6.04845 14.6293 6.29422V10.0001H16.3793C16.8302 9.98999 17.2701 10.1388 17.6222 10.4206C17.9743 10.7023 18.2161 11.0989 18.3052 11.5409C18.3501 11.8066 18.3366 12.0789 18.2655 12.3389C18.1945 12.5988 18.0675 12.8401 17.8936 13.046C17.7197 13.2519 17.503 13.4173 17.2586 13.5309C17.0142 13.6444 16.748 13.7033 16.4785 13.7034H14.6293V17.4076C14.6293 17.5292 14.6054 17.6497 14.5588 17.762C14.5122 17.8744 14.4439 17.9765 14.3579 18.0625C14.2718 18.1485 14.1697 18.2167 14.0572 18.2631C13.9448 18.3096 13.8243 18.3335 13.7027 18.3334H10.9252V16.4817C10.9252 16.2118 10.8662 15.9452 10.7525 15.7004C10.6387 15.4557 10.4728 15.2387 10.2665 15.0647C10.0601 14.8908 9.81824 14.764 9.55777 14.6932C9.29731 14.6224 9.02454 14.6094 8.75852 14.6551C8.31682 14.7451 7.92086 14.9876 7.63993 15.3401C7.359 15.6926 7.21101 16.1327 7.22185 16.5834V18.3334H2.59185C2.47024 18.3335 2.3498 18.3096 2.23742 18.2631C2.12504 18.2166 2.02294 18.1485 1.93694 18.0625C1.85095 17.9765 1.78276 17.8744 1.73627 17.762C1.68978 17.6496 1.66591 17.5292 1.66602 17.4076Z"
        fill="currentColor"
        fillOpacity="0.52"
      />
    </Box>
  );
}

/**
 * The wordmark's destination is `#`: that is what the published footer links
 * to, and guessing at the home page would be a change, not a reproduction. Its
 * `alt` is ours, the published image has none, which leaves the link nameless.
 * The small footer sets it `flush`, without the space under it.
 */
export function Wordmark({ flush = false }: { flush?: boolean }) {
  return (
    <StyledAnchor
      href={FOOTER_BRAND.wordmarkHref}
      sx={(theme) => ({
        width: WORDMARK.width,
        maxWidth: "100%",
        mb: flush ? 0 : 3,
        [theme.breakpoints.down(SITE_STEPS.tiny)]: { mb: 0 },
      })}
    >
      <Box
        component="img"
        src={FOOTER_BRAND.wordmarkSrc}
        alt="Webshare"
        sx={{
          ...WORDMARK,
          display: "inline-block",
          maxWidth: "100%",
          objectFit: "contain",
          verticalAlign: "middle",
        }}
      />
    </StyledAnchor>
  );
}

function OfferRow() {
  return (
    <StyledAnchor
      href={FOOTER_BRAND.offer.href}
      target="_blank"
      rel="noopener"
      sx={(theme) => ({
        ...ROW,
        columnGap: "6.4px",
        p: 3,
        backgroundColor: alpha(ACCENT, 0.12),
        border: `1px solid ${OFFER_EDGE}`,
        color: "primary.main",
        "&:hover": { borderColor: "primary.main" },
        [theme.breakpoints.down(SITE_STEPS.wide)]: {
          width: "100%",
          justifyContent: "center",
        },
      })}
    >
      <Typography component="p" sx={LABEL}>
        {FOOTER_BRAND.offer.lead}{" "}
        <Box component="span" sx={{ color: OFFER_HIGHLIGHT }}>
          {FOOTER_BRAND.offer.highlight}
        </Box>
      </Typography>
      <ExternalIcon />
    </StyledAnchor>
  );
}

function ExtensionRow() {
  return (
    <StyledAnchor
      href={FOOTER_BRAND.extension.href}
      target="_blank"
      rel="noopener"
      sx={(theme) => ({
        ...ROW,
        columnGap: 2.5,
        p: 2,
        // Invisible at rest, and the values the hover eases out of. Keeping
        // them is what lets a computed-style probe see one control.
        backgroundColor: alpha(ACCENT, 0),
        borderStyle: "none",
        borderColor: "rgba(240, 249, 247, 0)",
        color: marketingPalette.gray5,
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.03)" },
        [theme.breakpoints.down(SITE_STEPS.wide)]: {
          width: "100%",
          justifyContent: "center",
        },
      })}
    >
      <ExtensionIcon />
      <Typography component="p" sx={LABEL}>
        {FOOTER_BRAND.extension.label}
      </Typography>
      <ExternalIcon />
    </StyledAnchor>
  );
}

/** `.footer-left_new`: the column the wordmark heads, 360 wide and 16 clear of the links above the wide step. */
export function BrandColumn({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        rowGap: 2,
        maxWidth: 360,
        mr: 3,
        [theme.breakpoints.down(SITE_STEPS.wide)]: {
          width: "100%",
          maxWidth: "100%",
          mr: 0,
        },
      })}
    >
      {children}
    </Box>
  );
}

/** The wordmark and the two promotional rows under it. */
export default function FooterBrand() {
  return (
    <BrandColumn>
      <Wordmark />
      <OfferRow />
      <ExtensionRow />
    </BrandColumn>
  );
}
